import React, { FC, ReactElement } from "react";
import {
  Box,
  ClickAwayListener,
  Fade,
  Paper,
  PopperPlacementType,
  Theme,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import PopperUnstyled from "@mui/base/PopperUnstyled";

interface DropdownMenuProps {
  content: ReactElement;
  children: ReactElement;
  open: boolean;
  onClose: () => void;
  arrow?: boolean;
  placement?: PopperPlacementType;
  borderRadius?: number;
}

const useStyles = makeStyles((theme: Theme) => {
  const color = theme.palette.background.paper;
  return {
    popoverRoot: {
      backgroundColor: "transparent",
      maxWidth: 1000,
    },
    content: {},
    popper: {
      zIndex: 2000,
      '&[x-placement*="bottom"] $arrow': {
        top: 0,
        left: 0,
        marginTop: "-0.71em",
        marginLeft: 4,
        marginRight: 4,
        "&::before": {
          transformOrigin: "0 100%",
        },
      },
      '&[x-placement*="top"] $arrow': {
        bottom: 0,
        left: 0,
        marginBottom: "-0.71em",
        marginLeft: 4,
        marginRight: 4,
        "&::before": {
          transformOrigin: "100% 0",
        },
      },
      '&[x-placement*="right"] $arrow': {
        left: 0,
        marginLeft: "-0.71em",
        height: "1em",
        width: "0.71em",
        marginTop: 4,
        marginBottom: 4,
        "&::before": {
          transformOrigin: "100% 100%",
        },
      },
      '&[x-placement*="left"] $arrow': {
        right: 0,
        marginRight: "-0.71em",
        height: "1em",
        width: "0.71em",
        marginTop: 4,
        marginBottom: 4,
        "&::before": {
          transformOrigin: "0 0",
        },
      },
    },
    arrow: {
      overflow: "hidden",
      position: "absolute",
      width: "1em",
      height: "0.71em" /* = width / sqrt(2) = (length of the hypotenuse) */,
      boxSizing: "border-box",
      color,
      "&::before": {
        content: '""',
        margin: "auto",
        display: "block",
        width: "100%",
        height: "100%",
        boxShadow: theme.shadows[1],
        backgroundColor: "currentColor",
        transform: "rotate(45deg)",
      },
    },
  };
});

const DropdownMenu: FC<DropdownMenuProps> = ({
  placement,
  arrow,
  open,
  onClose,
  content,
  children,
  borderRadius,
}) => {
  const classes = useStyles();
  const [arrowRef, setArrowRef] = React.useState<HTMLElement | null>(null);
  const [childNode, setChildNode] = React.useState<HTMLElement | null>(null);
  return (
    <div>
      {React.cloneElement(children, { ...children.props, ref: setChildNode })}
      <PopperUnstyled
        open={open}
        anchorEl={childNode}
        placement={placement}
        transition
        className={classes.popper}
        modifiers={[
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              boundariesElement: "window",
            },
          },
          {
            name: "arrow",
            enabled: arrow || false,
            options: {
              element: arrowRef,
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={0}>
            <Paper
              elevation={0}
              style={{ borderRadius: borderRadius ? borderRadius : undefined }}
            >
              <ClickAwayListener onClickAway={onClose}>
                <Paper
                  elevation={0}
                  className={classes.popoverRoot}
                  style={{
                    borderRadius: borderRadius ? borderRadius : undefined,
                  }}
                >
                  {arrow ? (
                    <span className={classes.arrow} ref={setArrowRef} />
                  ) : null}
                  <Box className={classes.content}>{content}</Box>
                </Paper>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </PopperUnstyled>
    </div>
  );
};

export default DropdownMenu;
